import { createPhotoThumbnail } from './image-processing';

export const MAX_ATTACHMENT_INPUT_MB = 15;
export const MAX_ATTACHMENT_INPUT_BYTES = MAX_ATTACHMENT_INPUT_MB * 1024 * 1024;

const PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

const BLOCKED_EXTENSIONS = new Set([
  'app', 'appimage', 'apk', 'bat', 'bin', 'cmd', 'com', 'command', 'cpl', 'deb', 'dll',
  'dmg', 'drv', 'exe', 'gadget', 'hta', 'htm', 'html', 'jar', 'js', 'jse', 'lnk', 'mjs',
  'msi', 'msp', 'ocx', 'pif', 'pkg', 'ps1', 'psm1', 'reg', 'rpm', 'run', 'scf', 'scr',
  'sh', 'so', 'svg', 'sys', 'url', 'vb', 'vbe', 'vbs', 'wsf', 'wsh', 'xhtml', 'xml',
  'docm', 'dotm', 'xlsm', 'xltm', 'xlam', 'pptm', 'potm', 'ppam', 'ppsm', 'sldm',
]);

const BLOCKED_MIME_TYPES = new Set([
  'application/java-archive',
  'application/javascript',
  'application/x-bat',
  'application/x-dosexec',
  'application/x-executable',
  'application/x-httpd-php',
  'application/x-msdownload',
  'application/x-msdos-program',
  'application/x-powershell',
  'application/x-sh',
  'application/x-shellscript',
  'application/xhtml+xml',
  'image/svg+xml',
  'text/html',
  'text/javascript',
  'text/xml',
]);

export interface ProcessedNoteAttachment {
  originalBytes: Uint8Array;
  thumbnailBytes?: Uint8Array;
  mimeType: string;
}

function extensionFor(name: string): string {
  const normalized = name.trim().toLowerCase().replace(/[.\s]+$/g, '');
  const lastDot = normalized.lastIndexOf('.');
  return lastDot >= 0 ? normalized.slice(lastDot + 1) : '';
}

export function validateNoteAttachmentFile(file: File): void {
  if (file.size <= 0) throw new Error('The selected file is empty.');
  if (file.size > MAX_ATTACHMENT_INPUT_BYTES) {
    throw new Error(`Choose a file smaller than ${MAX_ATTACHMENT_INPUT_MB} MB.`);
  }

  const extension = extensionFor(file.name);
  const mimeType = file.type.trim().toLowerCase();
  if (BLOCKED_EXTENSIONS.has(extension) || BLOCKED_MIME_TYPES.has(mimeType)) {
    throw new Error('Executable, script, active web, and macro-enabled files are not allowed.');
  }
}

export async function processNoteAttachmentFile(file: File): Promise<ProcessedNoteAttachment> {
  validateNoteAttachmentFile(file);
  const originalBytes = new Uint8Array(await file.arrayBuffer());
  if (PHOTO_TYPES.has(file.type.toLowerCase())) {
    return {
      originalBytes,
      thumbnailBytes: await createPhotoThumbnail(file),
      mimeType: file.type.toLowerCase(),
    };
  }

  return {
    originalBytes,
    mimeType: file.type.trim().toLowerCase() || 'application/octet-stream',
  };
}

export function isInlineImageMime(mimeType: string): boolean {
  const normalized = mimeType.toLowerCase();
  return normalized.startsWith('image/') && normalized !== 'image/svg+xml';
}

export function isTextPreviewMime(mimeType: string): boolean {
  const normalized = mimeType.toLowerCase();
  return normalized.startsWith('text/') || normalized === 'application/json';
}

export function attachmentPreviewKind(mimeType: string): 'image' | 'pdf' | 'audio' | 'video' | 'text' | 'download' {
  const normalized = mimeType.toLowerCase();
  if (isInlineImageMime(normalized)) return 'image';
  if (normalized === 'application/pdf') return 'pdf';
  if (normalized.startsWith('audio/')) return 'audio';
  if (normalized.startsWith('video/')) return 'video';
  if (isTextPreviewMime(normalized)) return 'text';
  return 'download';
}
